import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
} from "antd";
import React, { FC } from "react";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import classes from "./style/FilterFrame.module.css";

import { useAppDispatch, useAppSelector } from "../../../store/reduxHooks";
import {
  FilterApplications,
  RESET_FILTER,
  UP_DATE_FILTER,
} from "../../../store/slices/applicationBankListSlice/applicationBankListSlice";

import { ApplicationBankListStatusPreset } from "../../../store/api/applicationBankListApi/applicationBank.api";
import {
  SELECT_APPLICATIONS_TYPE_BG,
  SELECT_FILTER,
} from "../../../store/selectors/applicatoinsSelector";
import { USER } from "../../../types";
import { useGetPermissionQuery } from "../../../store/api/permission.api";
import { SELECT_AVAILABLE_STATUSES } from "../../../store/selectors/authSliceLoginSelector";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import { debounce } from "lodash";

const buttonStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};
const styleWidth = { width: "100%" };

type StatusPresetFilterItem = {
  label: string;
  value: ApplicationBankListStatusPreset;
};

// опции фильтра по статусам заявок (по группам статусов)
const applicationBankStatusPresetOptions: Record<
  USER,
  StatusPresetFilterItem[]
> = {
  [USER.UNDERWRITER]: [
    { label: "Все", value: "" },
    { label: "Новые заявки", value: "new" },
    { label: "Активные заявки", value: "active" },
    { label: "Завершенные", value: "closed" },
    { label: "Мои заявки", value: "user_application" },
  ],
  [USER.EMPLOYEE]: [
    { label: "Все", value: "" },
    { label: "На выпуск", value: "for_release" },
    { label: "Активные заявки", value: "active" },
    { label: "Завершенные", value: "closed" },
  ],
  [USER.LEAD]: [
    { label: "Все", value: "" },
    { label: "Новые заявки", value: "new" },
    { label: "Активные заявки", value: "active" },
    { label: "Завершенные", value: "closed" },
    { label: "Отказ по стоп факторам", value: "stop_factor" },
  ],
};

// фильтр заявок
const FilterFrame: FC = () => {
  const dispatch = useAppDispatch();

  const [form] = useForm();

  const { data: permissionData } = useGetPermissionQuery();
  const userRole = permissionData?.data.user.type;

  const typeBgList = useAppSelector(SELECT_APPLICATIONS_TYPE_BG);
  const availableStatuses = useAppSelector(SELECT_AVAILABLE_STATUSES);
  const applicationBankListFilter = useAppSelector(SELECT_FILTER);

  const handleChangeFilter = (filterValues: Partial<FilterApplications>) => {
    dispatch(UP_DATE_FILTER(filterValues));
  };

  const handleChangeFilterDebounced = debounce(
    (filterValues: Partial<FilterApplications>) => {
      dispatch(UP_DATE_FILTER(filterValues));
    },
    700
  );

  const resetFilter = () => {
    dispatch(RESET_FILTER());
    form.resetFields();
  };

  return (
    <Form form={form}>
      <div className={classes.banksWrapper}>
        <Row gutter={8}>
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Form.Item
              label="Тип БГ"
              name="bg_type_id"
              style={{ marginBottom: "12px" }}
            >
              <Select
                placeholder="Тип банковской гарантии"
                allowClear
                value={applicationBankListFilter.bg_type}
                onChange={(value) => handleChangeFilter({ bg_type: value })}
              >
                {typeBgList?.map((bgType) => (
                  <Select.Option value={bgType.type} key={bgType.type}>
                    {bgType.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Form.Item
              label="Статус"
              name="status"
              style={{ marginBottom: "12px" }}
            >
              <Select
                placeholder="Все статусы"
                allowClear
                value={applicationBankListFilter.status}
                onChange={(value) => handleChangeFilter({ status: value })}
              >
                {availableStatuses?.map((status) => (
                  <Select.Option value={status.type} key={status.type}>
                    {status.title}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={12} md={12} lg={5} xl={4}>
            <Form.Item
              label="От"
              name="amount_from"
              style={{ marginBottom: "12px" }}
            >
              <InputNumber
                placeholder="Сумма"
                stringMode={true}
                controls={false}
                onChange={(value) => {
                  handleChangeFilterDebounced({
                    amount_from: value ? String(value) : null,
                  });
                }}
                style={{ width: "100%" }}
                suffix={"₽"}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={12} md={12} lg={5} xl={4}>
            <Form.Item
              name="amount_to"
              style={{ marginBottom: "12px" }}
              label="До"
            >
              <InputNumber
                placeholder="Сумма"
                stringMode={true}
                controls={false}
                onChange={(value) =>
                  handleChangeFilterDebounced({
                    amount_to: value ? String(value) : null,
                  })
                }
                style={{ width: "100%" }}
                suffix={"₽"}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Form.Item
              label="Дата создания"
              name="created_at"
              style={{ marginBottom: "12px" }}
            >
              <DatePicker
                value={applicationBankListFilter.created_at}
                onChange={(value) =>
                  handleChangeFilter({
                    created_at: value ? dayjs(value).format("YYYY-MM-DD") : "",
                  })
                }
                style={styleWidth}
                placeholder="2024-08-23"
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={11} md={11} lg={9} xl={6}>
            <Form.Item
              name="inn"
              className={classes.searchNumber}
              style={{ marginBottom: "12px" }}
            >
              <Input
                style={styleWidth}
                placeholder="ИНН организации (10-12 цифр)"
                suffix={<SearchOutlined />}
                allowClear
                value={applicationBankListFilter.inn}
                onChange={(e) =>
                  handleChangeFilterDebounced({ inn: e.target.value })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8} xl={4}>
            <Form.Item
              name="application_bank_id"
              className={classes.searchNumber}
            >
              <Input
                style={styleWidth}
                placeholder="Номер заявки"
                suffix={<SearchOutlined />}
                type="number"
                allowClear
                value={applicationBankListFilter.application_bank_id}
                onChange={(value) => {
                  value &&
                    handleChangeFilterDebounced({
                      application_bank_id: String(value.target.value),
                    });
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={6} sm={3} md={2} lg={2} xl={1}>
            <Button
              style={buttonStyles}
              onClick={resetFilter}
              icon={<CloseOutlined />}
            ></Button>
          </Col>
        </Row>

        {!!userRole && (
          <Radio.Group
            className={classes.applicationStatusList}
            options={applicationBankStatusPresetOptions[userRole]}
            optionType="button"
            defaultValue=""
            value={applicationBankListFilter.status_preset}
            onChange={(e) =>
              handleChangeFilter({ status_preset: e.target.value })
            }
          />
        )}
      </div>
    </Form>
  );
};

export default FilterFrame;
